/**
 * Dog Avenue — cart drawer line-items refresh patch
 *
 * Echo defers updating `.cart-drawer__line-items` with setTimeout(1250). Multiple
 * cart:change events queue multiple timeouts; an older HTML swap can run after a newer
 * one (e.g. accessory colour flicker). This patch cancels a pending swap when a new
 * cart:change arrives, without editing theme.js (upload this asset + theme.liquid tag).
 * Dog Avenue: delay is 0 for `baseEvent === 'da-cart-accessories'` so accessory picks
 * refresh line-item markup immediately (same idea as `variant:add`).
 *
 * Load as `type="module"` immediately after `theme.js` so `cart-drawer` is defined first.
 * If theme.js already includes the same logic, this patch is redundant but safe.
 */
function extractSectionId(element) {
	element = element.classList.contains('shopify-section')
		? element
		: element.closest('.shopify-section')
	return element.id.replace('shopify-section-', '')
}

function fadeOutChildren(container, durationMs) {
	if (!container || !container.children || container.children.length === 0) {
		return Promise.resolve()
	}
	var anims = []
	for (var i = 0; i < container.children.length; i++) {
		var ch = container.children[i]
		var a = ch.animate([{ opacity: 1 }, { opacity: 0 }], {
			duration: durationMs,
			fill: 'forwards',
		})
		anims.push(a.finished.catch(function () {}))
	}
	return Promise.all(anims)
}

function fadeInEmptyState(el, durationMs) {
	if (!el || !el.animate) return Promise.resolve()
	el.style.opacity = '0'
	var a = el.animate(
		[
			{ opacity: 0, transform: 'translateY(20px)' },
			{ opacity: 1, transform: 'translateY(0)' },
		],
		{ duration: durationMs, fill: 'forwards' },
	)
	return a.finished.catch(function () {})
}

async function patchedOnCartChanged(event) {
	if (this._lineItemsHtmlUpdateTimer != null) {
		clearTimeout(this._lineItemsHtmlUpdateTimer)
		this._lineItemsHtmlUpdateTimer = null
	}

	var updatedDrawerContent = new DOMParser().parseFromString(
		event.detail.cart['sections'][extractSectionId(this)],
		'text/html',
	)

	if (event.detail.cart['item_count'] > 0) {
		var currentLineItemsElement = this.querySelector('.cart-drawer__line-items')
		var updatedLineItemsElement = updatedDrawerContent.querySelector(
			'.cart-drawer__line-items',
		)
		if (!currentLineItemsElement) {
			this.replaceChildren(
				document
					.createRange()
					.createContextualFragment(
						updatedDrawerContent.querySelector('.cart-drawer').innerHTML,
					),
			)
		} else {
			var delay =
				event.detail.baseEvent === 'variant:add' ||
				event.detail.baseEvent === 'da-cart-accessories'
					? 0
					: 1250
			var self = this
			self._lineItemsHtmlUpdateTimer = setTimeout(function () {
				self._lineItemsHtmlUpdateTimer = null
				currentLineItemsElement.innerHTML = updatedLineItemsElement.innerHTML
			}, delay)
			var footerSlot = this.querySelector('[slot="footer"]')
			var newFooter = updatedDrawerContent.querySelector('[slot="footer"]')
			if (footerSlot && newFooter) {
				footerSlot.replaceChildren(...newFooter.childNodes)
			}
		}
	} else {
		await fadeOutChildren(this, 150)
		var drawerRoot = updatedDrawerContent.querySelector('.cart-drawer')
		if (drawerRoot) {
			this.replaceChildren(...drawerRoot.childNodes)
		}
		var empty = this.querySelector('.empty-state')
		await fadeInEmptyState(empty, 150)
	}
}

function applyPatch() {
	var ctor = customElements.get('cart-drawer')
	if (!ctor || ctor.prototype.__daCartDrawerLineItemsPatch) return
	ctor.prototype.__daCartDrawerLineItemsPatch = true

	var origDisc = ctor.prototype.disconnectedCallback
	ctor.prototype.disconnectedCallback = function () {
		if (this._lineItemsHtmlUpdateTimer != null) {
			clearTimeout(this._lineItemsHtmlUpdateTimer)
			this._lineItemsHtmlUpdateTimer = null
		}
		if (typeof origDisc === 'function') {
			return origDisc.call(this)
		}
	}

	ctor.prototype._onCartChanged = patchedOnCartChanged
}

if (customElements.get('cart-drawer')) {
	applyPatch()
} else {
	customElements.whenDefined('cart-drawer').then(applyPatch)
}
